import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { Control, Errors, Form } from 'react-redux-form';

import { CheckboxInline, Options, Select, DynamicSelectIdentifier } from '@common/components';
import { ContractViewModel, PropertyTypeViewModel } from '@models/Contract';
import {
  hasValue,
  hasAlpha,
  isValidCity,
  isValidZip,
  getDefaultErrorMessages,
  hasNumber,
  isNumeric
} from '@common/validators';
import { ModalProps } from '@common/components/modal/_components/modal.component';
import { AddressViewModel, OptionsViewModel } from '@models/common.models';
import { RouteDefinitions } from '@common/route-definitions';
import * as Redux from 'redux';
import FormNames from '@constants/FormNames.enum';
import { EditPermissions, EditableFields } from '@models/EditPermissions';
import { valueIsLessThanN } from '@common/validators';
import { dateParser } from '@common/dateParser';

export interface SaleType {
  propertySaleTypeCategory: {
    description: string;
    id: number;
    value: string;
  };
  id: number;
  value: string;
}

export interface PropertyInfoComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly message: string;
  readonly currentStep: number;
  readonly contactInitiators: Array<OptionsViewModel>;
  readonly contactMediums: Array<OptionsViewModel>;
  readonly contactTypes: Array<OptionsViewModel>;
  readonly propertyTypes: Array<PropertyTypeViewModel>;
  readonly propertySaleTypes: Array<OptionsViewModel>;
  readonly verifiableAddress?: AddressViewModel;
  readonly contractVM: ContractViewModel | null;
  readonly isOrderEdit: boolean;
  readonly isVerifying?: boolean;
  readonly isLoadingResale?: boolean;
  readonly isOpenContract?: boolean;
  readonly isQuote?: boolean;

  readonly clearPriceAdjustments: () => void;
  readonly getContactInitiators: () => void;
  readonly getContactMediums: () => void;
  readonly getContactTypes: () => void;
  readonly getPropertyTypes: () => void;
  readonly getPropertySaleTypes: () => void;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (model: ContractViewModel) => void;
  readonly setOrderType: (type: number) => void;
  readonly toggleModal: (modalProps: ModalProps) => void;
  readonly registerAddressVerifier: (shouldCheckDuplicates: boolean) => Promise<Redux.Unsubscribe | undefined>;
  readonly unregisterAddressVerifier: () => void;
  readonly updateCoverages: (
    event: ContractViewModel,
    zipCode?: string,
    propertySaleTypeId?: OptionsViewModel,
    propertyTypeId?: OptionsViewModel,
    yearBuilt?: string
  ) => void;
}

let addressListener: Redux.Unsubscribe | undefined | null = null;

export class PropertyInfoComponent extends React.Component<PropertyInfoComponentProperties> {
  private isContract = window.location.pathname.indexOf('contract') > -1;

  constructor(props: PropertyInfoComponentProperties) {
    super(props);

    // This binding is necessary to make `this` work in the callback
    this.isUnder5k = this.isUnder5k.bind(this);
    this.validatePropertyTypeWithYear = this.validatePropertyTypeWithYear.bind(this);
    this.handleSetOrderTypeEvent = this.handleSetOrderTypeEvent.bind(this);
  }

  componentDidMount() {
    this.props.getContactInitiators();
    this.props.getContactMediums();
    this.props.getContactTypes();
    this.props.getPropertyTypes();
    this.props.getPropertySaleTypes();
    if (!this.props.isOrderEdit) {
      const temp = this.props.registerAddressVerifier(!this.props.isOrderEdit);
      if (temp) {
        temp.then(unsubscribe => (addressListener = unsubscribe));
      }
    }
  }

  componentWillUnmount() {
    if (addressListener) {
      addressListener();
      addressListener = null;
    }
  }

  updateEditCoverages = (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    let eventValue = event.currentTarget.value as any;
    let eventName = event.currentTarget.name as string;
    if (this.props.isOrderEdit && this.props.updateCoverages && this.props.contractVM) {
      if (eventName.includes('zip')) {
        this.props.updateCoverages(this.props.contractVM, eventValue, undefined, undefined, undefined);
      } else if (eventName.includes('propertyType')) {
        var optionST = this.props.propertyTypes.find(o => o.id === +eventValue);
        this.props.updateCoverages(this.props.contractVM, undefined, undefined, optionST, undefined);
      } else if (eventName.includes('propertySaleType')) {
        var optionPT = this.props.propertySaleTypes.find(o => o.id === +eventValue);
        this.props.updateCoverages(this.props.contractVM, undefined, optionPT, undefined, undefined);
      } else if (eventName.includes('yearBuilt')) {
        var yearBuilt = eventValue;
        this.props.updateCoverages(this.props.contractVM, undefined, undefined, undefined, yearBuilt);
      }
    }
  };

  componentWillReceiveProps(newProps: PropertyInfoComponentProperties) {
    if (newProps.contractVM && this.props.contractVM) {
      if (newProps.contractVM.propertySaleType !== this.props.contractVM.propertySaleType) {
        this.handleSetOrderType(newProps.contractVM.propertySaleType.id);
      }
    }
  }

  renderError() {
    if (!this.props.message) {
      return;
    }
    return <div className="error">{this.props.message}</div>;
  }

  isUnder5k(propertyType: PropertyTypeViewModel) {
    let under5k = false;
    this.props.propertyTypes.forEach(type => {
      if (type.id === +propertyType.id) {
        under5k = !type.over5k;
      }
    });
    return under5k;
  }

  validatePropertyTypeWithYear(formValues: ContractViewModel): boolean {
    const propertyType = formValues.propertyType;
    if (!propertyType) {
      return true;
    }

    if (this.isUnder5k(propertyType)) {
      return true;
    }

    return !!formValues.yearBuilt;
  }

  handleSetOrderTypeEvent = (event: React.FormEvent<HTMLSelectElement>) => {
    const value = (event.target as HTMLSelectElement).value;
    this.handleSetOrderType(+value);
    if (this.props.isOrderEdit) {
      this.updateEditCoverages(event);
    } else if (this.props.isQuote) {
      this.props.clearPriceAdjustments();
    }
  };

  handleSetOrderType = (value: number) => {
    const saleType = this.props.propertySaleTypes.filter(obj => {
      return obj.id === value;
    })[0] as SaleType;
    if (!saleType) {
      return;
    }
    const category = (saleType.propertySaleTypeCategory || {}).id;
    if (this.props.propertySaleTypes) {
      this.props.setOrderType(category);
    }
  };

  renderCallerName = (): JSX.Element => {
    if (this.props.isOrderEdit) {
      return (
        <>
          <Control.text
            model=".contactName"
            maxLength={40}
            validators={{ required: hasValue, hasAlpha }}
            disabled={
              this.props.isOrderEdit &&
              !EditPermissions.CanEditField(EditableFields.ContactedBy, this.props.isOpenContract)
            }
            defaultValue={this.props.contractVM ? this.props.contractVM.contactName : '--'}
          />
          <Errors
            className="error"
            model=".contactName"
            show={{ touched: true, focus: false }}
            messages={getDefaultErrorMessages()}
          />
        </>
      );
    } else {
      return (
        <>
          <Control.text
            model=".contactName"
            maxLength={40}
            validators={{ required: hasValue, hasAlpha }}
            disabled={
              this.props.isOrderEdit &&
              !EditPermissions.CanEditField(EditableFields.ContactedBy, this.props.isOpenContract)
            }
          />
          <Errors
            className="error"
            model=".contactName"
            show={{ touched: true, focus: false }}
            messages={getDefaultErrorMessages()}
          />
        </>
      );
    }
  };

  checkLength = (val: string) => {
    let isValid = val ? val.length === 4 : true;
    return isValid;
  };

  isVerifying = (): boolean => {
    return !this.props.isVerifying || false;
  };

  render() {
    return (
      <Form
        model={FormNames.PropertyInfoForm}
        hideNativeErrors={true}
        onSubmit={this.props.onSaveClick}
        validators={{
          '': { isPropertyTypeOver5KAndYearRequired: this.validatePropertyTypeWithYear, isVerifying: this.isVerifying }
        }}
        validateOn="change"
      >
        <h2>Step {this.props.currentStep + 1}: Contact and Property Information</h2>
        <div className="row">
          {
            <div className="col-md-6 col-xs-12">
              <h4>Contact Information</h4>
              <div className="row">
                <div className="form-control col-xs-12">
                  <label htmlFor="propertyInfoForm.contactName">Caller's Name</label>
                  {this.props.isOrderEdit &&
                  !EditPermissions.CanEditField(EditableFields.ContactedBy, this.props.isOpenContract) &&
                  this.props.contractVM ? (
                    <text>
                      <br />
                      <br />
                      {this.props.contractVM.contactName}
                    </text>
                  ) : (
                    this.renderCallerName()
                  )}
                </div>
              </div>
              <div className="row">
                <div className="form-control col-md-4">
                  <label htmlFor="propertyInfoForm.contactType.id">Contacted By</label>
                  <Control.select
                    model=".contactType.id"
                    validators={{ selection: hasValue }}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.ContactType, this.props.isOpenContract)
                    }
                  >
                    <Options options={this.props.contactTypes} />
                  </Control.select>
                  <Errors
                    className="error"
                    model=".contactType.id"
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                </div>
                <div className="form-control col-md-4">
                  <label htmlFor="propertyInfoForm.contactMedium">Contacted Via</label>
                  <Control.select
                    model=".contactMedium.id"
                    validators={{ selection: hasValue }}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.ContactedVia, this.props.isOpenContract)
                    }
                  >
                    <Options options={this.props.contactMediums} />
                  </Control.select>
                  <Errors
                    className="error"
                    model=".contactMedium.id"
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                </div>
                <div className="form-control col-md-4">
                  <label htmlFor="propertyInfoForm.contactInit">Contact Represents</label>
                  <Control.select
                    model=".contactInit.id"
                    validators={{ selection: hasValue }}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.Represents, this.props.isOpenContract)
                    }
                  >
                    <Options options={this.props.contactInitiators} />
                  </Control.select>
                  <Errors
                    className="error"
                    model=".contactInit.id"
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                </div>
              </div>
            </div>
          }
          <div className={`col-md-${this.props.isOrderEdit ? 12 : 6} col-xs-12 dark-gray-bc`}>
            <div className="row top-xs between-xs">
              <h4>Property Information</h4>
              {this.isContract && !this.props.isOrderEdit ? (
                <Link to={RouteDefinitions.quote.Create} className="btn btn-primary">
                  Generate Quote
                </Link>
              ) : (
                ''
              )}
            </div>
            <div className="row">
              <div className="form-control col-xs-12">
                <label htmlFor="propertyInfoForm.propertyAddress.line1">Address 1</label>
                <Control.text
                  model=".propertyAddress.line1"
                  validators={{ required: hasValue, hasAlpha: hasAlpha, hasNumber }}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.Address, this.props.isOpenContract)
                  }
                />
                <Errors
                  className="error"
                  model=".propertyAddress.line1"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="form-control col-xs-12">
                <label htmlFor="propertyInfoForm.propertyAddress.line2">Address 2</label>
                <Control.text
                  model=".propertyAddress.line2"
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.Address, this.props.isOpenContract)
                  }
                />
                <Errors
                  className="error"
                  model=".propertyAddress.line2"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="col-xs-12" hidden={true}>
                <CheckboxInline>
                  <Control.checkbox model=".mailReturned" />
                  <label className="checkbox-inline" htmlFor="propertyInfoForm.mailReturned">
                    Mail Returned
                  </label>
                </CheckboxInline>
              </div>
            </div>
            <div className="row mt-10">
              <div className="form-control col-md-6">
                <label htmlFor="propertyInfoForm.propertyAddress.city">City</label>
                <Control.text
                  model=".propertyAddress.city"
                  validators={{ required: hasValue, isValidCity }}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.Address, this.props.isOpenContract)
                  }
                />
                <Errors
                  className="error"
                  model=".propertyAddress.city"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="form-control col-md-3">
                <label htmlFor="propertyInfoForm.propertyAddress.state.abbreviation">State</label>
                <Select
                  model=".propertyAddress.state.abbreviation"
                  validators={{ required: hasValue }}
                  identifier={DynamicSelectIdentifier.USStates}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.Address, this.props.isOpenContract)
                  }
                />
                <Errors
                  className="error"
                  model=".propertyAddress.state.abbreviation"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className=" form-control col-md-3">
                <label htmlFor="propertyInfoForm.propertyAddress.zip">Zip</label>
                <Control.text
                  model=".propertyAddress.zip"
                  onBlur={(event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                    this.updateEditCoverages(event);
                  }}
                  validators={{ required: hasValue, isValidZip: isValidZip }}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.Address, this.props.isOpenContract)
                  }
                />
                <Errors
                  className="error"
                  model=".propertyAddress.zip"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
            </div>

            <div className="row mb-10">
              <div className="form-control col-md-5">
                <label htmlFor="propertyInfoForm.propertyType">Property Type</label>
                <Control.select
                  model=".propertyType.id"
                  validators={{ selection: hasValue }}
                  onChange={(
                    event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
                  ) => {
                    this.updateEditCoverages(event);
                  }}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.PropertyType, this.props.isOpenContract)
                  }
                >
                  <Options options={this.props.propertyTypes} />
                </Control.select>
                <Errors
                  className="error"
                  model=".propertyType.id"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
              <div className="form-control col-md-3">
                <label htmlFor="propertyInfoForm.yearBuilt">Year Built</label>
                <Control.text
                  model=".yearBuilt"
                  maxLength={4}
                  minLength={4}
                  validators={{
                    length: val => this.checkLength(val),
                    isNumeric,
                    isValidBuildYear: valueIsLessThanN(
                      +dateParser
                        .now()
                        .add(2, 'y')
                        .format('YYYY')
                    )
                  }}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.YearBuilt, this.props.isOpenContract)
                  }
                  onBlur={(event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                    this.updateEditCoverages(event);
                  }}
                />
                <Errors
                  className="error"
                  model=".yearBuilt"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages({
                    length: 'Enter a valid year',
                    isValidBuildYear:
                      'Enter a year <= ' +
                      dateParser
                        .now()
                        .add(1, 'y')
                        .format('YYYY')
                  })}
                />
                {/* <div className="col-md-10"> */}
                <Errors
                  className="error"
                  model={FormNames.PropertyInfoForm}
                  show={true}
                  messages={getDefaultErrorMessages({
                    isPropertyTypeOver5KAndYearRequired: 'Year is required for Property types over 5k.'
                  })}
                />
                {/* </div> */}
              </div>
              <div className="form-control col-md-4">
                <label htmlFor="propertyInfoForm.propertySaleType">Sale Type</label>
                <Control.select
                  model=".propertySaleType.id"
                  validators={{ selection: hasValue }}
                  onChange={this.handleSetOrderTypeEvent}
                  disabled={
                    this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.SaleType, this.props.isOpenContract)
                  }
                >
                  <Options options={this.props.propertySaleTypes} />
                </Control.select>
                <Errors
                  className="error"
                  model=".propertySaleType.id"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
            </div>
            <Errors
              className="error"
              model={FormNames.PropertyInfoForm}
              show={true}
              messages={getDefaultErrorMessages({
                isVerifying: 'Address is currently being verified'
              })}
            />
          </div>
        </div>
        <div>
          <div className="col-md-1">
            <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
              Save
            </button>
          </div>
        </div>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/property-info/property-info.component.tsx